import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useClientId } from '../../../../../../_hooks/useClientId';
import { ACCOUNTS_QUERY_KEY } from '../../../../AccountsConstants';
import { useAccountBeneficiariesMutation } from '../../../../api/useAccountBeneficiariesMutation';
import { useAccountPortfolioMutation } from '../../../../api/useAccountPortfolioMutation';
import { AccountPortfolioDTO } from '../../../../api/useAccountPortfolioQuery';
import { useAccountSaveMutation } from '../../../../api/useAccountSaveMutation';
import {
  ACCOUNT_OPTIONS_QUERY_KEY,
  DistributionStrategyTypeDTO,
} from '../../../../api/useAccountsOptionsQuery';
import { ACCOUNT_PREFERENCES_QUERY_KEY } from '../../../../api/useAccountsQuery';
import { useAccountFormContext } from './context/useAccountFormContext';
import {
  accountPortfolioDataToForm,
  accountPortfolioFormToBody,
  ACCOUNTS_WITH_BENEFICIARIES_TAB,
  bankAccountBeneficiariesFormToBeneficiariesBody,
  bankAccountsFormToBody,
  dataToForm,
  defaultFormValues,
  FormFields,
  getAccountHasAssetAllocationTab,
  LocalSaveOptions,
  validation,
} from './AccountForm.meta';
import { queryKeys as pulseCheckQueryKeys } from '../../../../../../PulseCheck/api/queryKeys';
import { queryKeys as charitableGivingWorkflowQueryKeys } from '../../../../../../CharitableGivingWorkflow/stages/CharitableGiving/api/queryKeys';
import { AccountModalUsageContext } from './context/AccountFormProvider.meta';

interface Params {
  accountId?: string;
  accountPortfolioData?: AccountPortfolioDTO;
  localSaveOptions?: LocalSaveOptions;
}

export const useAccountForm = ({ accountId, accountPortfolioData }: Params) => {
  const { accounts, options, usageContext, localSaveOptions } = useAccountFormContext();

  const queryClient = useQueryClient();

  const clientId = useClientId();

  const {
    error: saveAccountErrors,
    isLoading: isSavingAccount,
    mutateAsync: saveBankAccounts,
  } = useAccountSaveMutation();

  const {
    error: saveAccountPortfolioErrors,
    isLoading: isSavingAccountPortfolio,
    mutateAsync: saveAccountPortfolio,
  } = useAccountPortfolioMutation();

  const {
    error: saveAccountBeneficiariesErrors,
    isLoading: isSavingAccountBeneficiaries,
    mutateAsync: saveAccountBeneficiaries,
  } = useAccountBeneficiariesMutation();

  const selectedAccount = localSaveOptions
    ? localSaveOptions.data?.bankAccount
    : accounts.find(({ AccountId }) => AccountId === accountId);

  const accountPortfolio = localSaveOptions
    ? localSaveOptions.data?.accountPortfolio
    : accountPortfolioData;

  const {
    OwnerOptions,
    ContributionStartYearOptions,
    ContributionEndYearOptions,
    CustomDistributionStrategyOptions,
    BeneficiariesOptions: beneficiariesOptions,
  } = options;

  const bankAccountDefaultValues = selectedAccount
    ? dataToForm(selectedAccount, options)
    : {
        ...defaultFormValues,
        AccountOwner: { label: OwnerOptions.People[0], value: OwnerOptions.People[0] },
        ContributionStartYear: ContributionStartYearOptions[0],
        ContributionEndYear: ContributionEndYearOptions[0],
        DistributionStrategyType: DistributionStrategyTypeDTO.None,
        DistributionStrategy: {
          ComputationYearType: CustomDistributionStrategyOptions.computationYearType[0].value,
          DistributionStartYearType: 0,
          DistributionEndYearType: 0,
          StartDistributionsToday: true,
          StartDistributionsAtRetirement: false,
          EndDistributionsAtRetirement: true,
          EndOfPlan: false,
          NoOtherDistributionsAllowed: false,
        },
      };

  const formBag = useForm<FormFields>({
    defaultValues: {
      bankAccount: bankAccountDefaultValues,
      ...(accountPortfolio
        ? {
            accountPortfolio: accountPortfolioDataToForm({
              accountType: selectedAccount?.AccountType,
              accountPortfolio,
              accountOptions: options,
            }),
          }
        : {}),
    },
    resolver: yupResolver(validation),

    context: {
      allowAssetBucketingCustomization: options.AllowAssetBucketingCustomization,
      assetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
    },
  });

  const saveAccount = async (data: FormFields) => {
    const response = await saveBankAccounts(bankAccountsFormToBody(data.bankAccount));

    if (response) {
      formBag.setValue('bankAccount.AccountId', response.AccountId);
    }

    const accountPortfolioBody = accountPortfolioFormToBody(data.accountPortfolio);

    if (
      getAccountHasAssetAllocationTab(data.bankAccount.AccountType, {
        annuityType: data.bankAccount.AnnuityType,
      }) &&
      accountPortfolioBody &&
      response
    ) {
      await saveAccountPortfolio({
        ...accountPortfolioBody,
        AccountId: formBag.watch('bankAccount.AccountId') || response.AccountId,
      });
    }

    if (
      typeof data.bankAccount.AccountType === 'number' &&
      ACCOUNTS_WITH_BENEFICIARIES_TAB.includes(data.bankAccount.AccountType) &&
      data.bankAccount.AccountBeneficiaries &&
      response
    ) {
      await saveAccountBeneficiaries({
        ...bankAccountBeneficiariesFormToBeneficiariesBody(
          data.bankAccount.AccountBeneficiaries,
          beneficiariesOptions,
        ),
        AccountId: formBag.watch('bankAccount.AccountId') || response.AccountId,
      });
    }

    if (
      usageContext !== AccountModalUsageContext.CharitableGivingWorkflow &&
      usageContext !== AccountModalUsageContext.DafAccounts
    ) {
      await Promise.all([
        queryClient.invalidateQueries([ACCOUNTS_QUERY_KEY, ACCOUNT_OPTIONS_QUERY_KEY, clientId]),
        queryClient.invalidateQueries([
          ACCOUNTS_QUERY_KEY,
          ACCOUNT_PREFERENCES_QUERY_KEY,
          clientId,
        ]),
        queryClient.invalidateQueries(pulseCheckQueryKeys.goalsOptionsAll()),
      ]);
    }

    if (usageContext === AccountModalUsageContext.DafAccounts) {
      queryClient.invalidateQueries(charitableGivingWorkflowQueryKeys.dafAccounts(clientId));
    }

    if (usageContext === AccountModalUsageContext.CharitableGivingWorkflow) {
      queryClient.invalidateQueries(charitableGivingWorkflowQueryKeys.accountsAll());
    }
  };

  return {
    data: {
      formBag,
      errors: saveAccountErrors || saveAccountPortfolioErrors || saveAccountBeneficiariesErrors,
      isSaving: isSavingAccount || isSavingAccountPortfolio || isSavingAccountBeneficiaries,
    },
    actions: {
      saveAccount,
    },
  };
};
